import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import {
  IAccountReceivableCreate,
  IAccountReceivablesWithPayments,
  ICreateReceivedPaymentRequestParams,
  IUpdateAccountReceivableRequestParams,
} from "../../domain";
import { partnersService } from "../../services/partner.service";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IUpdateReceivedPaymentRequestParams } from "../../domain/IUpdateReceivedPaymentRequestParams";
import { IUpdateReceivablePlanRequstParams } from "../../domain/IUpdateReceivablePlanRequstParams";
import { IResponseAccountReceivablesAdditionalInfo } from "../../domain/receivables/IResponseAccountReceivablesAdditionalInfo";
import { IAcknowledgeRequestParams } from "../../domain/receivables/IAcknowledgeRequestParams";
import { resetPartnerPlansReceivable } from "../Main/main.slice";

interface accountReceivableState {
  loading: boolean;
  accountReceivablesWithPayments: IAccountReceivablesWithPayments[];
  allAccReceivablesWithPayments: IAccountReceivablesWithPayments[];
  receivablesTotalInvoicedAmountForPartner: number;
  receivablesTotalPaidAmountForPartner: number;
  receivablesTotalDebtAmountForPartner: number;
  receivablesTotalCashPaidAmountForPartner: number;
  receivablesNextDueDateForPartner?: string;
  receivablesAdditionalInfo?: IResponseAccountReceivablesAdditionalInfo;
  newAccountReceivablePopover: IPopoverState;
  additionalInfoPopover: IPopoverState;
  newAccountReceivableModalLoading: boolean;
  deleteAccountReceivableModalOpen: boolean;
  indexForDeletionAccountReceivable: number;
  newReceivedPaymentModalOpen: boolean;
  deleteReceivedPaymentModalOpen: boolean;
  indexesForPaymentDeletion?: {
    accountIndex: number;
    paymentIndex: number;
  };
  indexForPaymentCreation: number;
  indexForInvoice: number;
  invoicePopover: IPopoverState;
  paymentCreationPopover: IPopoverState;
  error: any;
}

const initialState: accountReceivableState = {
  loading: false,
  accountReceivablesWithPayments: [],
  allAccReceivablesWithPayments: [],
  receivablesTotalInvoicedAmountForPartner: 0,
  receivablesTotalPaidAmountForPartner: 0,
  receivablesTotalDebtAmountForPartner: 0,
  receivablesTotalCashPaidAmountForPartner: 0,
  receivablesNextDueDateForPartner: undefined,
  receivablesAdditionalInfo: undefined,
  newAccountReceivablePopover: {
    isOpen: false,
    top: 0,
    left: 0,
  },
  additionalInfoPopover: {
    isOpen: false,
    top: 0,
    left: 0,
  },
  newAccountReceivableModalLoading: false,
  deleteAccountReceivableModalOpen: false,
  indexForDeletionAccountReceivable: -1,
  newReceivedPaymentModalOpen: false,
  deleteReceivedPaymentModalOpen: false,
  indexesForPaymentDeletion: undefined,
  indexForPaymentCreation: -1,
  indexForInvoice: 0,
  invoicePopover: {
    isOpen: false,
    top: 0,
    left: 0,
  },
  paymentCreationPopover: {
    isOpen: false,
    top: 0,
    left: 0,
  },
  error: undefined,
};

export const getAllAccountReceivables = createAsyncThunk(
  "Partners/getAllAccountReceivables",
  async (partnerId: number) => {
    try {
      return await partnersService.getAllAccountReceivables(partnerId);
    } catch (err) {
      setError(err);
    }
  }
);

export const createAccountReceivable = createAsyncThunk(
  "Partners/createAccountReceivable",
  async (data: IAccountReceivableCreate, { rejectWithValue }) => {
    try {
      return await partnersService.createAccountReceivable(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const updateAccountReceivable = createAsyncThunk(
  "Partners/updateAccountReceivable",
  async (data: IUpdateAccountReceivableRequestParams, { rejectWithValue }) => {
    try {
      return await partnersService.updateAccountReceivable(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const deleteAccountReceivable = createAsyncThunk(
  "Partners/deleteAccountReceivable",
  async (accountReceivableId: number, { rejectWithValue }) => {
    try {
      return await partnersService.deleteAccountReceivable(accountReceivableId);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const createReceivedPayment = createAsyncThunk(
  "Partners/createReceivedPayment",
  async (data: ICreateReceivedPaymentRequestParams, { rejectWithValue }) => {
    try {
      return await partnersService.createReceivedPayment(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const updateReceivedPayment = createAsyncThunk(
  "Partners/updateReceivedPayment",
  async (data: IUpdateReceivedPaymentRequestParams, { rejectWithValue }) => {
    try {
      return await partnersService.updateReceivedPayment(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const deleteReceivedPayment = createAsyncThunk(
  "Partners/deleteReceivedPayment",
  async (receivedPaymentId: number, { rejectWithValue }) => {
    try {
      return await partnersService.deleteReceivedPayment(receivedPaymentId);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const getReceivablesAdditionalInfo = createAsyncThunk(
  "Partners/getReceivablesAdditionalInfo",
  async (partnerId: number) => {
    try {
      return await partnersService.getReceivablesAdditionalInfo(partnerId);
    } catch (err) {
      setError(err);
    }
  }
);

export const updateReceivablesAdditionalInfoForConcrete = createAsyncThunk(
  "Partners/updateReceivablesAdditionalInfoForConcrete",
  async (data: IAcknowledgeRequestParams, { rejectWithValue }) => {
    try {
      return await partnersService.updateReceivablesAdditionalInfoForConcrete(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const updateReceivablesAdditionalInfoForStock = createAsyncThunk(
  "Partners/updateReceivablesAdditionalInfoForStock",
  async (data: IAcknowledgeRequestParams, { rejectWithValue }) => {
    try {
      return await partnersService.updateReceivablesAdditionalInfoForStock(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const accountReceivableSlice = createSlice({
  name: "partners-accountReceivable",
  initialState: initialState,
  reducers: {
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setAccountReceivables: (
      state,
      action: PayloadAction<IAccountReceivablesWithPayments[]>
    ) => {
      state.accountReceivablesWithPayments = action.payload;

      // Recompute totals from the (filtered) list so TableFooter stays in sync
      // with the rows shown. Mirrors the backend total calculation.
      let invoiced = 0;
      let paid = 0;
      let debt = 0;
      let cashPaid = 0;

      for (const item of action.payload) {
        const amountInvoiced = item.accountReceivable.amountInvoiced;
        const amountPaid = item.accountReceivable.amountPaid;
        const amountCash = item.receivedPayments.reduce(
          (sum, payment) => sum + (payment.isCash ? payment.amount : 0),
          0
        );

        invoiced += amountInvoiced;
        paid += amountPaid;
        debt += amountInvoiced - amountPaid;
        cashPaid += amountCash;
      }

      state.receivablesTotalInvoicedAmountForPartner = invoiced;
      state.receivablesTotalPaidAmountForPartner = paid;
      state.receivablesTotalDebtAmountForPartner = debt;
      state.receivablesTotalCashPaidAmountForPartner = cashPaid;
    },
    setNewAccountReceivablePopover: (
      state,
      action: PayloadAction<IPopoverState>
    ) => {
      state.newAccountReceivablePopover = action.payload;
    },
    setAdditionalInfoPopover: (
      state,
      action: PayloadAction<IPopoverState>
    ) => {
      state.additionalInfoPopover.isOpen = action.payload.isOpen;
      if (action.payload.left !== undefined) {
        state.additionalInfoPopover.left = action.payload.left;
      }
      if (action.payload.top !== undefined) {
        state.additionalInfoPopover.top = action.payload.top;
      }
    },
    setIndexForDeletionAccountReceivable: (
      state,
      action: PayloadAction<number>
    ) => {
      state.indexForDeletionAccountReceivable = action.payload;
      state.deleteAccountReceivableModalOpen = action.payload !== -1;
    },
    setIndexForPaymentCreation: (state, action: PayloadAction<number>) => {
      state.indexForPaymentCreation = action.payload;
      state.newReceivedPaymentModalOpen = action.payload !== -1;
    },
    setIndexesForPaymentDeletion: (
      state,
      action: PayloadAction<
        { accountIndex: number; paymentIndex: number } | undefined
      >
    ) => {
      state.indexesForPaymentDeletion = action.payload;
      state.deleteReceivedPaymentModalOpen = action.payload !== undefined;
    },
    updateAccountReceivableTemporarily: (
      state,
      action: PayloadAction<IUpdateAccountReceivableRequestParams>
    ) => {
      for (let receivableIndex = 0; receivableIndex < state.accountReceivablesWithPayments.length; receivableIndex++) {
        if (state.accountReceivablesWithPayments[receivableIndex].accountReceivable.receivableId === action.payload.accountReceivableId) {
          if (action.payload.invoiceId !== undefined) {
            state.accountReceivablesWithPayments[receivableIndex].accountReceivable.invoiceId = action.payload.invoiceId;
          }
          if (action.payload.projectId !== undefined) {
            state.accountReceivablesWithPayments[receivableIndex].accountReceivable.projectId = action.payload.projectId;
          }
          if (action.payload.invoiceDate !== undefined) {
            state.accountReceivablesWithPayments[receivableIndex].accountReceivable.invoiceDate = action.payload.invoiceDate;
          }
          if (action.payload.dueDate !== undefined) {
            state.accountReceivablesWithPayments[receivableIndex].accountReceivable.dueDate = action.payload.dueDate;
          }
          if (action.payload.amountInvoiced !== undefined) {
            const oldAmountInvoiced = state.accountReceivablesWithPayments[receivableIndex].accountReceivable.amountInvoiced;
            state.receivablesTotalInvoicedAmountForPartner += action.payload.amountInvoiced - oldAmountInvoiced;
            state.receivablesTotalDebtAmountForPartner += action.payload.amountInvoiced - oldAmountInvoiced;
            state.accountReceivablesWithPayments[receivableIndex].accountReceivable.amountInvoiced = action.payload.amountInvoiced;
          }
          if (action.payload.note !== undefined) {
            state.accountReceivablesWithPayments[receivableIndex].accountReceivable.note = action.payload.note;
          }
        }
      }
    },
    updateReceivablePlanTemporarily: (
      state,
      action: PayloadAction<IUpdateReceivablePlanRequstParams>
    ) => {
      for (let receivableIndex = 0; receivableIndex < state.accountReceivablesWithPayments.length; receivableIndex++) {
        if (state.accountReceivablesWithPayments[receivableIndex].accountReceivable.receivableId === action.payload.accountReceivableId) {
          state.accountReceivablesWithPayments[receivableIndex].planned = action.payload.planned;
        }
      }
    },
    updateReceivedPaymentTemporarily: (
      state,
      action: PayloadAction<IUpdateReceivedPaymentRequestParams>
    ) => {
      const receivable = state.accountReceivablesWithPayments.find(
        item => item.accountReceivable.receivableId === action.payload.accountReceivableId
      );

      if (!receivable) return;

      const payment = receivable.receivedPayments.find(
        p => p.receivedPaymentId === action.payload.receivedPaymentId
      );

      if (!payment) return;

      const oldAmount = payment.amount;
      const newAmount = action.payload.amount ?? oldAmount;
      const wasCash = payment.isCash;
      const isCash = action.payload.isCash ?? wasCash;
      const amountDifference = newAmount - oldAmount;

      state.receivablesTotalCashPaidAmountForPartner +=
        (isCash ? newAmount : 0) - (wasCash ? oldAmount : 0);

      if (action.payload.amount !== undefined) {
        state.receivablesTotalPaidAmountForPartner += amountDifference;
        state.receivablesTotalDebtAmountForPartner -= amountDifference;
        receivable.accountReceivable.amountPaid += amountDifference;
        payment.amount = newAmount;
      }

      if (action.payload.isCash !== undefined) payment.isCash = isCash;
      if (action.payload.date !== undefined) payment.date = action.payload.date;
    },
    setNote: (
      state,
      action: PayloadAction<{ receivableId: number; note: string }>
    ) => {
      const itemToUpdate = state.accountReceivablesWithPayments.find(
        (element) =>
          element.accountReceivable.receivableId === action.payload.receivableId
      );

      if (itemToUpdate) {
        itemToUpdate.accountReceivable.note = action.payload.note;
      }
    },
    setInvoicePopover: (state, action: PayloadAction<IPopoverState>) => {
      state.invoicePopover = action.payload;
    },
    setPaymentCreationPopover: (
      state,
      action: PayloadAction<IPopoverState>
    ) => {
      state.paymentCreationPopover = action.payload;
    },
    setIndexForInvoice: (state, action: PayloadAction<number>) => {
      state.indexForInvoice = action.payload;
    },
    updateAcknowledgedConcreteItemTemporarily: (state, action: PayloadAction<IAcknowledgeRequestParams>) => {
      if (state.receivablesAdditionalInfo !== undefined) {
        for (let index = 0; index < state.receivablesAdditionalInfo.concrete.items.length; index++) {
          const item = state.receivablesAdditionalInfo.concrete.items[index];
          if (item.outputItemId === action.payload.outputItemId && item.outputId === action.payload.outputId) {
            state.receivablesAdditionalInfo.concrete.items[index].isAcknowledged = action.payload.isAcknowledged;
          }
        }
      }
    },
    updateAcknowledgedStockItemTemporarily: (state, action: PayloadAction<IAcknowledgeRequestParams>) => {
      if (state.receivablesAdditionalInfo !== undefined) {
        for (let index = 0; index < state.receivablesAdditionalInfo.stock.items.length; index++) {
          const item = state.receivablesAdditionalInfo.stock.items[index];
          if (item.outputItemId === action.payload.outputItemId && item.outputId === action.payload.outputId) {
            state.receivablesAdditionalInfo.stock.items[index].isAcknowledged = action.payload.isAcknowledged;
          }
        }

      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllAccountReceivables.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllAccountReceivables.fulfilled, (state, action) => {
      if (action.payload) {
        state.accountReceivablesWithPayments =
          action.payload.accountReceivablesWithPayments;
        state.allAccReceivablesWithPayments =
          action.payload.accountReceivablesWithPayments;
        state.receivablesTotalInvoicedAmountForPartner =
          action.payload.receivablesTotalInvoicedAmountForPartner;
        state.receivablesTotalPaidAmountForPartner =
          action.payload.receivablesTotalPaidAmountForPartner;
        state.receivablesTotalDebtAmountForPartner =
          action.payload.receivablesTotalDebtAmountForPartner;
        state.receivablesTotalCashPaidAmountForPartner =
          action.payload.receivablesTotalCashPaidAmountForPartner;
        state.receivablesNextDueDateForPartner =
          action.payload.receivablesNextDueDateForPartner;
      }
      state.loading = false;
    });
    builder.addCase(getAllAccountReceivables.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(createAccountReceivable.pending, (state) => {
      state.newAccountReceivableModalLoading = true;
    });
    builder.addCase(createAccountReceivable.fulfilled, (state, action) => {
      state.newAccountReceivableModalLoading = false;
      state.newAccountReceivableModalLoading = false;
      if (action.payload) {
        state.accountReceivablesWithPayments = [
          action.payload,
          ...state.accountReceivablesWithPayments
        ]
        state.allAccReceivablesWithPayments = [
          action.payload,
          ...state.allAccReceivablesWithPayments
        ]
      }
      state.receivablesTotalInvoicedAmountForPartner += action.payload.accountReceivable.amountInvoiced;
      state.receivablesTotalDebtAmountForPartner += action.payload.accountReceivable.amountInvoiced;
    });
    builder.addCase(createAccountReceivable.rejected, (state, action) => {
      state.newAccountReceivableModalLoading = false;
      state.error = action.error;
    });
    builder.addCase(updateAccountReceivable.pending, (state) => {
    });
    builder.addCase(updateAccountReceivable.fulfilled, (state, action) => {
      if (action.payload) {
        state.accountReceivablesWithPayments =
          state.accountReceivablesWithPayments.map((acc) => {
            if (acc.accountReceivable.receivableId === action.payload.receivableId) {
              state.receivablesTotalInvoicedAmountForPartner +=
                action.payload.amountInvoiced - acc.accountReceivable.amountInvoiced;
              state.receivablesTotalDebtAmountForPartner +=
                action.payload.amountInvoiced - acc.accountReceivable.amountInvoiced;
              return {
                ...acc,
                accountReceivable: action.payload,
              };
            } else {
              return acc;
            }
          }
          );
        state.allAccReceivablesWithPayments =
          state.allAccReceivablesWithPayments.map((acc) => {
            if (acc.accountReceivable.receivableId === action.payload.receivableId) {
              state.receivablesTotalInvoicedAmountForPartner +=
                action.payload.amountInvoiced - acc.accountReceivable.amountInvoiced;
              state.receivablesTotalDebtAmountForPartner +=
                action.payload.amountInvoiced - acc.accountReceivable.amountInvoiced;
              return {
                ...acc,
                accountReceivable: action.payload,
              };
            } else {
              return acc;
            }
          }
          );
      }
    });
    builder.addCase(updateAccountReceivable.rejected, (state, action) => {
      state.error = action.error;
    });
    builder.addCase(createReceivedPayment.pending, (state) => {
      state.newAccountReceivableModalLoading = true;
    });
    builder.addCase(createReceivedPayment.fulfilled, (state, action) => {
      state.newAccountReceivableModalLoading = false;
      if (action.payload) {
        state.accountReceivablesWithPayments =
          state.accountReceivablesWithPayments.map((acc) =>
            acc.accountReceivable.receivableId === action.payload.accountReceivableId
              ? {
                accountReceivable: {
                  ...acc.accountReceivable,
                  amountPaid: acc.accountReceivable.amountPaid + action.payload.amount
                },
                project: acc.project,
                receivedPayments: [action.payload, ...acc.receivedPayments],
                planned: acc.planned,
              }
              : acc
          );
        state.allAccReceivablesWithPayments =
          state.allAccReceivablesWithPayments.map((acc) =>
            acc.accountReceivable.receivableId === action.payload.accountReceivableId
              ? {
                accountReceivable: {
                  ...acc.accountReceivable,
                  amountPaid: acc.accountReceivable.amountPaid + action.payload.amount
                },
                project: acc.project,
                receivedPayments: [action.payload, ...acc.receivedPayments],
                planned: acc.planned,
              }
              : acc
          );
        state.receivablesTotalPaidAmountForPartner += action.payload.amount;
        state.receivablesTotalDebtAmountForPartner -= action.payload.amount;
        if (action.payload.isCash) {
          state.receivablesTotalCashPaidAmountForPartner += action.payload.amount;
        }
      }
    });
    builder.addCase(createReceivedPayment.rejected, (state, action) => {
      state.newAccountReceivableModalLoading = false;
      state.error = action.error;
    });
    builder.addCase(getReceivablesAdditionalInfo.fulfilled, (state, action) => {
      if (action.payload) {
        state.receivablesAdditionalInfo = action.payload;
      }
    });
    builder.addCase(updateReceivablesAdditionalInfoForConcrete.fulfilled, (state, action) => {
      if (state.receivablesAdditionalInfo !== undefined) {
        for (let index = 0; index < state.receivablesAdditionalInfo.concrete.items.length; index++) {
          const item = state.receivablesAdditionalInfo.concrete.items[index];
          if (item.outputItemId === action.payload.outputItemId && item.outputId === action.payload.outputId) {
            state.receivablesAdditionalInfo.concrete.items[index] = action.payload;
          }
        }
      }
    });
    builder.addCase(updateReceivablesAdditionalInfoForStock.fulfilled, (state, action) => {
      if (state.receivablesAdditionalInfo !== undefined) {
        for (let index = 0; index < state.receivablesAdditionalInfo.stock.items.length; index++) {
          const item = state.receivablesAdditionalInfo.stock.items[index];
          if (item.outputItemId === action.payload.outputItemId && item.outputId === action.payload.outputId) {
            state.receivablesAdditionalInfo.stock.items[index] = action.payload;
          }
        }
      }
    });
    builder.addCase(resetPartnerPlansReceivable.fulfilled, (state, action) => {
      if (action.payload) {
        state.accountReceivablesWithPayments.forEach(receivable => {
          receivable.planned = false;
        });
      }
    });
  },
});

export const {
  setAccountReceivables,
  setNewAccountReceivablePopover,
  setAdditionalInfoPopover,
  setError,
  setIndexForDeletionAccountReceivable,
  setIndexForPaymentCreation,
  setIndexesForPaymentDeletion,
  updateAccountReceivableTemporarily,
  updateReceivablePlanTemporarily,
  updateReceivedPaymentTemporarily,
  setNote,
  setInvoicePopover,
  setIndexForInvoice,
  setPaymentCreationPopover,
  updateAcknowledgedStockItemTemporarily,
  updateAcknowledgedConcreteItemTemporarily,
} = accountReceivableSlice.actions;

export const selectState = (state: RootState) =>
  state.partners.accountReceivable;

export const selectAccountReceivableForDeletion = (state: RootState) =>
  state.partners.accountReceivable.accountReceivablesWithPayments[
  state.partners.accountReceivable.indexForDeletionAccountReceivable
  ];

export const selectAccountReceivableForPaymentCreation = (state: RootState) =>
  state.partners.accountReceivable.accountReceivablesWithPayments[
    state.partners.accountReceivable.indexForPaymentCreation
  ]?.accountReceivable;

export const selectReceivablePaymentForDeletion = (state: RootState) => {
  if (state.partners.accountReceivable.indexesForPaymentDeletion) {
    const account =
      state.partners.accountReceivable.accountReceivablesWithPayments[
      state.partners.accountReceivable.indexesForPaymentDeletion.accountIndex
      ];
    const reference = account.accountReceivable.invoiceId;
    const payment =
      account.receivedPayments[
      state.partners.accountReceivable.indexesForPaymentDeletion.paymentIndex
      ];
    return {
      reference,
      payment,
    };
  }
  return null;
};

export default accountReceivableSlice.reducer;
